/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import Modal from "@components/common/Modal/Modal";
import Button from "@components/common/Button/Button";
import Input from "@components/common/Input/Input";
import Htag from "@components/common/Htag/Htag";
import P from "@components/common/P/P";
import SvgIcon from "@components/common/SvgIcon/SvgIcon";
import {
	CarrierIcon,
	CarrierThreeIcon,
	CarrierTwoIcon,
	GoogleSignInIcon,
	LogoLightNavIcon,
	SenderIcon,
} from "@assets/icons";
import { useToast } from "context/ToastContext";
import Spinner from "@components/Spinner";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/routes";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "store/store";
import { loginThunk } from "store/thunks/authThunks";
import {
	forgotPassword,
	googleAuth,
	updatePassword,
} from "@services/authService";
import { setAuthData } from "store/slices/authSlice";
import { useTranslation } from "react-i18next";

interface LoginProcessProps {
	isModalOpen: boolean;
	onClose: () => void;
}

declare global {
	interface Window {
		google?: any;
	}
}

type LoginStep = 1 | 2 | 3 | 4 | 5 | 6;
type GoogleFinalRoleID = 3 | 4 | 5 | 6;
type GoogleSelectableRoleID = 2 | GoogleFinalRoleID;
type GoogleAuthRole = {
	role: "sender" | "carrier";
	role_id: GoogleFinalRoleID;
};

const LoginProcess: React.FC<LoginProcessProps> = ({
	isModalOpen,
	onClose,
}) => {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const dispatch = useDispatch<AppDispatch>();

	const [step, setStep] = useState<LoginStep>(1);
	const [email, setEmail] = useState("");
	const [phone, setPhone] = useState("");
	const [password, setPassword] = useState("");
	const [otp, setOtp] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isLoading, setLoading] = useState(false);
	const [usePhone, setUsePhone] = useState(false);
	const [googleRoleID, setGoogleRoleID] =
		useState<GoogleSelectableRoleID | null>(null);
	const selectedGoogleRoleRef = useRef<GoogleAuthRole | null>(null);

	useEffect(() => {
		if (!isModalOpen) return;

		setStep(1);
		setEmail("");
		setPhone("");
		setPassword("");
		setOtp("");
		setNewPassword("");
		setConfirmPassword("");
		setUsePhone(false);
		setLoading(false);
		setGoogleRoleID(null);
		selectedGoogleRoleRef.current = null;
	}, [isModalOpen]);

	const toastSuccess = useCallback(
		(message: string) =>
			showToast({
				message,
				status: "success",
				position: "top-center",
				autoClose: true,
				duration: 3000,
			}),
		[showToast],
	);

	const toastError = useCallback(
		(message: string) =>
			showToast({
				message,
				status: "error",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			}),
		[showToast],
	);

	const toastWarn = useCallback(
		(message: string) =>
			showToast({
				message,
				status: "warn",
				position: "top-right",
				autoClose: true,
				duration: 3000,
			}),
		[showToast],
	);

	const handleLogin = useCallback(
		(
			username: string,
			secret: string,
			credType: "email" | "phone" = "email",
		) => {
			setLoading(true);

			dispatch(loginThunk({ username, password: secret, credType }))
				.unwrap()
				.then(() => {
					toastSuccess(t("auth.toast.loginSuccess"));
					navigate(ROUTES.MAIN);
				})
				.catch((error: Error) => {
					toastError(error.message || t("auth.toast.loginFailed"));
				})
				.finally(() => setLoading(false));
		},
		[dispatch, navigate, t, toastError, toastSuccess],
	);

	const handleForgotOtpContinue = useCallback(() => {
		const credentials = usePhone ? phone.trim() : email.trim();
		const code = otp.trim();

		if (!credentials) {
			toastWarn(t("auth.toast.enterEmailOrPhone"));
			setStep(1);
			return;
		}

		if (!code) {
			toastWarn(t("auth.toast.enterOtp"));
			return;
		}

		setStep(4);
	}, [usePhone, phone, email, otp, t, toastWarn]);

	const handleUpdateForgotPassword = useCallback(async () => {
		const credentials = usePhone ? phone.trim() : email.trim();
		const code = otp.trim();
		const nextPassword = newPassword.trim();
		const confirmedPassword = confirmPassword.trim();

		if (!credentials) {
			toastWarn(t("auth.toast.enterEmailOrPhone"));
			setStep(1);
			return;
		}

		if (!code) {
			toastWarn(t("auth.toast.enterOtp"));
			setStep(3);
			return;
		}

		if (!nextPassword || !confirmedPassword) {
			toastWarn(t("auth.register.toast.enterAndConfirmPassword"));
			return;
		}

		if (nextPassword !== confirmedPassword) {
			toastWarn(t("auth.register.toast.passwordsMismatch"));
			return;
		}

		setLoading(true);

		try {
			await updatePassword({
				otp: code,
				password: nextPassword,
				...(usePhone ? { phone: credentials } : { email: credentials }),
			});
			toastSuccess(t("auth.toast.passwordUpdatedSuccess"));
			onClose();
		} catch (error) {
			toastError(
				(error as Error).message || t("auth.toast.passwordUpdateFailed"),
			);
		} finally {
			setLoading(false);
		}
	}, [
		usePhone,
		phone,
		email,
		otp,
		newPassword,
		confirmPassword,
		t,
		toastWarn,
		toastSuccess,
		toastError,
		onClose,
	]);

	const handleNextStep = useCallback(() => {
		if (step === 1) {
			const value = usePhone ? phone.trim() : email.trim();

			if (!value) {
				toastWarn(t("auth.toast.enterEmailOrPhone"));
				return;
			}

			setStep(2);
			return;
		}

		if (step === 2) {
			const pwd = password.trim();

			if (!pwd) {
				toastWarn(t("auth.toast.enterPassword"));
				return;
			}

			if (usePhone) {
				handleLogin(phone.trim(), pwd, "phone");
				return;
			}

			handleLogin(email.trim(), pwd, "email");
			return;
		}

		if (step === 3) {
			handleForgotOtpContinue();
			return;
		}

		void handleUpdateForgotPassword();
	}, [
		step,
		usePhone,
		phone,
		email,
		password,
		handleLogin,
		handleForgotOtpContinue,
		handleUpdateForgotPassword,
		t,
		toastWarn,
	]);

	const handleForgotPassword = useCallback(async () => {
		const credentials = usePhone ? phone.trim() : email.trim();

		if (!credentials) {
			toastWarn(t("auth.toast.enterEmailOrPhone"));
			setStep(1);
			return;
		}

		setLoading(true);

		try {
			await forgotPassword(credentials, usePhone ? "phone" : "email");
			toastSuccess(t("auth.toast.forgotPasswordSuccess"));
			setOtp("");
			setNewPassword("");
			setConfirmPassword("");
			setStep(3);
		} catch (error) {
			toastError(
				(error as Error).message || t("auth.toast.forgotPasswordFailed"),
			);
		} finally {
			setLoading(false);
		}
	}, [usePhone, phone, email, t, toastWarn, toastSuccess, toastError]);

	const handleBack = useCallback(() => {
		if (step === 2) {
			setStep(1);
			return;
		}

		if (step === 3) {
			setStep(2);
			return;
		}

		if (step === 4) {
			setStep(3);
			return;
		}

		if (step === 5) {
			setGoogleRoleID(null);
			setStep(1);
			return;
		}

		if (step === 6) {
			setGoogleRoleID(2);
			setStep(5);
		}
	}, [step]);

	const getGoogleAuthRole = useCallback(
		(roleID: GoogleFinalRoleID): GoogleAuthRole => ({
			role: roleID === 3 ? "sender" : "carrier",
			role_id: roleID,
		}),
		[],
	);

	const [gisReady, setGisReady] = useState(false);

	useEffect(() => {
		if (!isModalOpen) return;

		const id = setInterval(() => {
			if (
				window.google?.accounts?.id &&
				process.env.REACT_APP_GOOGLE_CLIENT_ID
			) {
				window.google.accounts.id.initialize({
					client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID!,
					callback: async (resp: any) => {
						try {
							setLoading(true);
							const idToken = resp?.credential;
							if (!idToken) throw new Error("Google did not return id_token");

							const selectedGoogleRole = selectedGoogleRoleRef.current;
							if (!selectedGoogleRole) {
								throw new Error(t("auth.register.toast.pickUserType"));
							}

							const data = await googleAuth({
								...selectedGoogleRole,
								id_token: idToken,
							});

							dispatch(setAuthData(data));
							toastSuccess(t("auth.toast.loginSuccess"));
							navigate(ROUTES.MAIN);
						} catch (e) {
							toastError((e as Error).message || t("auth.toast.googleFailed"));
						} finally {
							selectedGoogleRoleRef.current = null;
							setLoading(false);
						}
					},
				});

				setGisReady(true);
				clearInterval(id);
			}
		}, 200);

		return () => clearInterval(id);
	}, [isModalOpen, dispatch, navigate, t, toastError, toastSuccess]);

	const startGoogleLogin = useCallback(
		(roleID: GoogleFinalRoleID) => {
			if (!window.google?.accounts?.id) {
				toastError(t("auth.toast.googleNotLoaded"));
				return;
			}
			if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
				toastError(t("auth.toast.googleMissingClientId"));
				return;
			}

			selectedGoogleRoleRef.current = getGoogleAuthRole(roleID);

			window.google.accounts.id.prompt((n: any) => {
				if (n?.isNotDisplayed?.())
					console.log("GIS not displayed:", n.getNotDisplayedReason());
				if (n?.isSkippedMoment?.())
					console.log("GIS skipped:", n.getSkippedReason());
				if (n?.isDismissedMoment?.())
					console.log("GIS dismissed:", n.getDismissedReason());
			});
		},
		[getGoogleAuthRole, t, toastError],
	);

	const handleGoogleLogin = useCallback(() => {
		if (!window.google?.accounts?.id) {
			toastError(t("auth.toast.googleNotLoaded"));
			return;
		}
		if (!process.env.REACT_APP_GOOGLE_CLIENT_ID) {
			toastError(t("auth.toast.googleMissingClientId"));
			return;
		}

		setGoogleRoleID(null);
		setStep(5);
	}, [t, toastError]);

	const handleGoogleRoleContinue = useCallback(() => {
		if (!googleRoleID) {
			toastWarn(t("auth.register.toast.pickUserType"));
			return;
		}

		if (googleRoleID === 2) {
			setGoogleRoleID(null);
			setStep(6);
			return;
		}

		startGoogleLogin(googleRoleID);
	}, [googleRoleID, startGoogleLogin, t, toastWarn]);

	const handleGoogleCarrierRoleContinue = useCallback(() => {
		if (googleRoleID !== 4 && googleRoleID !== 5) {
			toastWarn(t("auth.register.toast.pickUserType"));
			return;
		}

		startGoogleLogin(googleRoleID);
	}, [googleRoleID, startGoogleLogin, t, toastWarn]);

	const stepVariant = useMemo(
		() => ({
			initial: { opacity: 0, x: -30 },
			animate: { opacity: 1, x: 0 },
			exit: { opacity: 0, x: 50 },
		}),
		[],
	);

	const header = useMemo(() => {
		if (step !== 1) {
			return {
				left: (
					<P
						size="p3"
						type="semibold"
						className="cursor-pointer text-light-grayThree dark:text-dark-grayThree"
						onClick={handleBack}
					>
						{t("common.back")}
					</P>
				),
				center: <SvgIcon Icon={LogoLightNavIcon} width={86} height={32} />,
			};
		}

		return {
			center: <SvgIcon Icon={LogoLightNavIcon} width={86} height={32} />,
		};
	}, [step, handleBack, t]);

	const step1Hint = useMemo(
		() => (usePhone ? t("auth.login.enterPhone") : t("auth.login.enterEmail")),
		[usePhone, t],
	);

	const step1Label = useMemo(
		() => (usePhone ? t("auth.login.phoneLabel") : t("auth.login.emailLabel")),
		[usePhone, t],
	);

	const step1Placeholder = useMemo(
		() =>
			usePhone
				? t("auth.login.phonePlaceholder")
				: t("auth.login.emailPlaceholder"),
		[usePhone, t],
	);

	const switchHint = useMemo(
		() => (usePhone ? t("auth.login.useEmail") : t("auth.login.usePhone")),
		[usePhone, t],
	);

	const step2Label = useMemo(() => t("auth.login.passwordLabel"), [t]);

	const step2Placeholder = useMemo(
		() => t("auth.login.passwordPlaceholder"),
		[t],
	);

	const otpStepHint = useMemo(
		() =>
			t("auth.register.otpSentTo", {
				target: usePhone
					? t("auth.register.target.phone")
					: t("auth.register.target.email"),
			}),
		[usePhone, t],
	);

	const primaryBtnText = useMemo(() => {
		if (step === 1) return t("auth.login.continue");
		if (step === 2) return t("auth.login.login");
		if (step === 3) return t("auth.register.confirm");
		return t("auth.login.updatePassword");
	}, [step, t]);

	const currentCredential = useMemo(
		() => (usePhone ? phone : email),
		[usePhone, phone, email],
	);

	const buttonLoader = useMemo(
		() => (
			<Spinner size={18} color="text-light-grayOne dark:text-dark-grayOne" />
		),
		[],
	);

	const renderStep = useMemo(
		() => (
			<motion.div
				key={step}
				variants={stepVariant}
				initial="initial"
				animate="animate"
				exit="exit"
				transition={{ duration: 0.3 }}
			>
				{step === 1 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-rows-max">
							<Htag tag="h3" type="semibold" className="text-center">
								{t("auth.login.welcome")}
							</Htag>

							<P
								size="p3"
								type="regular"
								className="text-center text-light-grayThree dark:text-dark-grayThree"
							>
								{step1Hint}
							</P>
						</div>

						<Input
							type={usePhone ? "tel" : "email"}
							label={step1Label}
							value={usePhone ? phone : email}
							onChange={(e) =>
								usePhone ? setPhone(e.target.value) : setEmail(e.target.value)
							}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleNextStep();
							}}
							placeholder={step1Placeholder}
						/>

						<P
							size="p3"
							type="regular"
							className="text-center cursor-pointer"
							onClick={() => setUsePhone((prev) => !prev)}
						>
							{switchHint}
						</P>

						<div className="grid gap-6 place-items-center grid-cols-frMaxFr">
							<hr className="w-full border-t border-light-strokeLanding dark:border-dark-strokeLanding" />
							<P
								size="t1"
								type="semibold"
								className="text-light-grayThree dark:text-dark-grayThree"
							>
								{t("common.or")}
							</P>
							<hr className="w-full border-t border-light-strokeLanding dark:border-dark-strokeLanding" />
						</div>

						<div
							className="grid gap-3 grid-cols-maxTwo place-items-center place-content-center py-2.5 px-4 border border-light-strokeLanding dark:border-dark-strokeLanding rounded-xl cursor-pointer"
							onClick={handleGoogleLogin}
						>
							<SvgIcon Icon={GoogleSignInIcon} width={24} height={24} />
							<P size="p3" type="semibold" className="cursor-pointer">
								{t("auth.login.continueWithGoogle")}
							</P>
						</div>

						<Button
							onClick={handleNextStep}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
						>
							{t("auth.login.continue")}
						</Button>
					</div>
				) : step === 2 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<Htag tag="h3" type="semibold" className="text-center">
							{currentCredential}
						</Htag>

						<div className="grid grid-flow-row grid-cols-1 gap-2 auto-rows-max">
							<Input
								type="password"
								label={step2Label}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleNextStep();
								}}
								placeholder={step2Placeholder}
							/>

							<button
								type="button"
								onClick={handleForgotPassword}
								disabled={isLoading}
								className="justify-self-end text-sm font-semibold text-light-mainColor dark:text-dark-mainColor transition-opacity duration-300 hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
							>
								{t("auth.login.forgotPassword")}
							</button>
						</div>

						<Button
							onClick={handleNextStep}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={isLoading}
							icon={isLoading ? buttonLoader : undefined}
						>
							{isLoading ? t("common.loading") : primaryBtnText}
						</Button>
					</div>
				) : step === 3 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-rows-max">
							<Htag tag="h3" type="semibold" className="text-center">
								{currentCredential}
							</Htag>

							<P
								size="p3"
								type="regular"
								className="text-center text-light-grayThree dark:text-dark-grayThree"
							>
								{otpStepHint}
							</P>
						</div>

						<Input
							type="text"
							label={t("auth.login.otpLabel")}
							value={otp}
							onChange={(e) => setOtp(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter") handleNextStep();
							}}
							placeholder={t("auth.login.otpPlaceholder")}
						/>

						<Button
							onClick={handleNextStep}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={isLoading}
							icon={isLoading ? buttonLoader : undefined}
						>
							{isLoading ? t("common.loading") : primaryBtnText}
						</Button>
					</div>
				) : step === 4 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-rows-max">
							<Htag tag="h3" type="semibold" className="text-center">
								{t("auth.register.createPasswordTitle")}
							</Htag>

							<P
								size="p3"
								type="regular"
								className="text-center text-light-grayThree dark:text-dark-grayThree"
							>
								{currentCredential}
							</P>
						</div>

						<div className="grid grid-flow-row grid-cols-1 gap-4 auto-rows-max">
							<Input
								type="password"
								label={t("auth.register.passwordCreateLabel")}
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleNextStep();
								}}
								placeholder={t("auth.register.passwordCreatePlaceholder")}
							/>

							<Input
								type="password"
								label={t("auth.register.passwordConfirmLabel")}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleNextStep();
								}}
								placeholder={t("auth.register.passwordConfirmPlaceholder")}
							/>
						</div>

						<Button
							onClick={handleNextStep}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={isLoading}
							icon={isLoading ? buttonLoader : undefined}
						>
							{isLoading ? t("common.loading") : primaryBtnText}
						</Button>
					</div>
				) : step === 5 ? (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<div className="grid grid-flow-row grid-cols-1 gap-1 auto-rows-max">
							<Htag tag="h3" type="semibold" className="text-center">
								{t("auth.register.welcome")}
							</Htag>
							<P size="p2" type="regular">
								{t("auth.register.whichUserType")}
							</P>
						</div>

						<div className="grid gap-8 grid-cols-maxThree place-items-center place-content-center">
							<div
								className="grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center"
								onClick={() => setGoogleRoleID(3)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${googleRoleID === 3 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={SenderIcon}
										width={46}
										height={46}
										className={`${googleRoleID === 3 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${googleRoleID === 3 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.sender.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${googleRoleID === 3 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.roles.sender.subtitle")}
									</P>
								</div>
							</div>

							<div
								className="grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center"
								onClick={() => setGoogleRoleID(2)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${googleRoleID === 2 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierIcon}
										width={46}
										height={46}
										className={`${googleRoleID === 2 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${googleRoleID === 2 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.carrier.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${googleRoleID === 2 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.roles.carrier.subtitle")}
									</P>
								</div>
							</div>

							<div
								className="grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center max-w-28"
								onClick={() => setGoogleRoleID(6)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${googleRoleID === 6 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierThreeIcon}
										width={46}
										height={46}
										className={`${googleRoleID === 6 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${googleRoleID === 6 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.logisticsCompany.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${googleRoleID === 6 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.roles.logisticsCompany.subtitle")}
									</P>
								</div>
							</div>
						</div>

						<Button
							onClick={handleGoogleRoleContinue}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={!googleRoleID || isLoading}
							icon={isLoading ? buttonLoader : undefined}
						>
							{isLoading ? t("common.loading") : t("common.continue")}
						</Button>
					</div>
				) : (
					<div className="grid grid-flow-row grid-cols-1 gap-5 auto-rows-max">
						<Htag tag="h3" type="semibold" className="text-center">
							{t("auth.register.whichCarrierType")}
						</Htag>

						<div className="grid gap-6 grid-cols-maxTwo place-items-center place-content-center">
							<div
								className="grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center max-w-28"
								onClick={() => setGoogleRoleID(4)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${googleRoleID === 4 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierIcon}
										width={46}
										height={46}
										className={`${googleRoleID === 4 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${googleRoleID === 4 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.carrier.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${googleRoleID === 4 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.carrierTypes.personal")}
									</P>
								</div>
							</div>

							<div
								className="grid grid-cols-1 grid-flow-row auto-rows-max gap-4 place-items-center max-w-28"
								onClick={() => setGoogleRoleID(5)}
							>
								<div
									className={`border p-4 rounded-xl cursor-pointer ${googleRoleID === 5 ? "border-light-mainColor dark:border-dark-mainColor" : "border-light-stroke dark:border-dark-stroke"}`}
								>
									<SvgIcon
										Icon={CarrierTwoIcon}
										width={46}
										height={46}
										className={`${googleRoleID === 5 ? "fill-light-mainColor dark:fill-dark-mainColor" : "fill-light-grayThree dark:fill-dark-grayThree"}`}
									/>
								</div>
								<div className="grid grid-flow-row grid-cols-1 gap-1">
									<P
										size="p3"
										type="semibold"
										className={`text-center ${googleRoleID === 5 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-textColor dark:text-dark-textColor"}`}
									>
										{t("auth.register.roles.carrier.title")}
									</P>
									<P
										size="t1"
										type="regular"
										className={`text-center ${googleRoleID === 5 ? "text-light-mainColor dark:text-dark-mainColor" : "text-light-grayThree dark:text-dark-grayThree"}`}
									>
										{t("auth.register.carrierTypes.fleetOwner")}
									</P>
								</div>
							</div>
						</div>

						<Button
							onClick={handleGoogleCarrierRoleContinue}
							appearance="primary"
							rounded="rounded-xl"
							width="full"
							disabled={(googleRoleID !== 4 && googleRoleID !== 5) || isLoading}
							icon={isLoading ? buttonLoader : undefined}
						>
							{isLoading ? t("common.loading") : t("common.continue")}
						</Button>
					</div>
				)}
			</motion.div>
		),
		[
			step,
			stepVariant,
			isLoading,
			usePhone,
			phone,
			email,
			password,
			otp,
			newPassword,
			confirmPassword,
			handleNextStep,
			handleForgotPassword,
			handleGoogleLogin,
			handleGoogleRoleContinue,
			handleGoogleCarrierRoleContinue,
			googleRoleID,
			t,
			step1Hint,
			step1Label,
			step1Placeholder,
			switchHint,
			step2Label,
			step2Placeholder,
			otpStepHint,
			primaryBtnText,
			currentCredential,
			buttonLoader,
		],
	);

	return (
		<Modal
			isOpen={isModalOpen}
			onClose={onClose}
			header={header}
			containerClassName="max-h-[calc(100vh-5vh)]"
		>
			{renderStep}
		</Modal>
	);
};

export default React.memo(LoginProcess);
